/**
 * Configurations saved on this device (§14.4 "Sharing": saving on the device is enough for
 * Alpha 1; shareable codes and links come later). Host only; plain localStorage.
 */
import type { TimerConfig } from "@handshake/config";
import { readJson, writeLocal } from "./storage.ts";

export interface SavedConfig {
  readonly name: string;
  readonly savedAt: number;
  /** The lobby overrides, as sent with `configure`. */
  readonly overrides: Readonly<Record<string, unknown>>;
  readonly playerCount: number | null;
  readonly timers: TimerConfig;
}

const KEY = "handshake:saved-configs";
const MAX = 20;

function isSaved(x: unknown): x is SavedConfig {
  const s = x as SavedConfig | null;
  return (
    !!s &&
    typeof s.name === "string" &&
    typeof s.savedAt === "number" &&
    typeof s.overrides === "object" &&
    s.overrides !== null &&
    typeof s.timers === "object" &&
    s.timers !== null
  );
}

/** Saved configurations, newest first. */
export function listSavedConfigs(): SavedConfig[] {
  const raw = readJson<unknown>(KEY);
  return Array.isArray(raw) ? raw.filter(isSaved).sort((a, b) => b.savedAt - a.savedAt) : [];
}

/** Saves (or replaces, by name) a configuration. */
export function saveConfig(entry: SavedConfig): SavedConfig[] {
  const name = entry.name.trim().slice(0, 40);
  if (!name) return listSavedConfigs();
  const list = [{ ...entry, name }, ...listSavedConfigs().filter((s) => s.name !== name)].slice(
    0,
    MAX,
  );
  writeLocal(KEY, JSON.stringify(list));
  return list;
}

export function deleteSavedConfig(name: string): SavedConfig[] {
  const list = listSavedConfigs().filter((s) => s.name !== name);
  writeLocal(KEY, list.length > 0 ? JSON.stringify(list) : null);
  return list;
}
